import React, { useEffect, useRef } from 'react'

import type { NextRouter } from 'next/router'
import { withRouter } from 'next/router'
import { HiOutlineClipboard } from 'react-icons/hi2'
import { v4 as uuidv4 } from 'uuid'

import type { User, WithAuthInfoProps } from '@sweetauth/core'
import { withAuthInfo } from '@sweetauth/core'

import Button from '@/components/common/Button'

import { SUPPORT_EMAIL } from '@/constants/general'

import { reportDatadogError } from '@/lib/datadog'

/*
!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

1. DO NOT ADD `ClientErrorBoundary` TO ANY COMPONENTS THAT ARE NOT PAGES.
3. DO NOT USE `ClientErrorBoundary` ON ANY PAGES THAT REQUIRE SSR.
2. DO NOT WRAP ANY HTML META TAGS (e.g. next-seo, next-head) WITHIN THE `ClientErrorBoundary`.
  - Doing this will cause SEO to fail and the meta tags to not be correctly fetched for SSR.

!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
*/

type ClientErrorBoundaryProps = WithAuthInfoProps & {
  children: React.ReactNode
  router: NextRouter
}

type ErrorBoundaryState = {
  hasError: boolean
  error: unknown
  errorId: string | null
  componentStack: string
}

const createInitialState = (): ErrorBoundaryState => ({
  hasError: false,
  error: null,
  errorId: null,
  componentStack: '',
})

class ClientErrorBoundaryInner extends React.Component<
  ClientErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = createInitialState()

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorId: uuidv4(),
      componentStack: '',
    }
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    const errorId = this.state.errorId ?? uuidv4()
    const componentStack = info.componentStack ?? ''
    this.setState({ componentStack, errorId })

    if (typeof window !== 'undefined') {
      reportDatadogError(error, {
        boundary: 'ClientErrorBoundary',
        errorId,
        componentStack,
        pathname: this.props.router?.asPath,
        userId: this.props.user?.userId,
      })
    }
  }

  private readonly reset = () => {
    this.setState(createInitialState())
    if (this.props.router) {
      void this.props.router.push('/')
    } else if (typeof window !== 'undefined') {
      window.location.href = '/'
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      const ErrorFallback = MakeErrorFallback(this.props.user)
      return (
        <ErrorFallback
          error={this.state.error}
          errorId={this.state.errorId}
          componentStack={this.state.componentStack}
          resetError={this.reset}
        />
      )
    }

    return this.props.children
  }
}

interface ErrorFallbackProps {
  error: unknown
  componentStack: string
  errorId: string | null
  resetError(): void
}

const copyDigest = async (errorId: string) => {
  await navigator.clipboard.writeText(errorId)
  alert('Error ID copied to clipboard.')
}

const isErrorWithProps = (
  err: unknown,
): err is { name: string; cause: unknown; message: string; stack?: string } => {
  return (
    typeof err === 'object' && err !== null && 'name' in err && 'message' in err
  )
}

const MakeErrorFallback = (user: User | null) => {
  const ErrorFallback = ({
    error,
    errorId,
    resetError,
    componentStack,
  }: ErrorFallbackProps) => {
    const lastSeenEventId = useRef<string | null>(null)

    useEffect(() => {
      if (errorId && errorId !== lastSeenEventId.current) {
        lastSeenEventId.current = errorId

        console.error(error)
      }
    }, [errorId])

    return (
      <div className="flex items-center justify-center w-full h-full">
        <div className="flex flex-col items-center justify-center p-10 gap-y-6 rounded-lg max-w-3xl">
          <div className="flex flex-col items-start justify-center text-md font-normal text-primary-900 dark:text-primary-100">
            <span className="pb-4 text-4xl text-accent-400 font-black dark:text-accent">
              Oh no...
            </span>
            <span>An unexpected error has occurred!</span>
            <span>
              If this issue persists, please reach out to&nbsp;
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="underline hover:opacity-70"
              >
                {SUPPORT_EMAIL}
              </a>
              &nbsp;for support.
            </span>
          </div>
          {errorId && (
            <div className="hidden md:flex items-center gap-x-2 rounded-md bg-accent-100 dark:bg-accent-600/10 w-full p-4">
              <pre className="text-accent-400 dark:text-accent-500 break-all">{`ID: ${errorId}`}</pre>
              <button
                onClick={() => {
                  void copyDigest(errorId)
                }}
                className="mb-[1px] transition-opacity duration-100 hover:opacity-70"
              >
                <HiOutlineClipboard className="w-4 h-4 text-accent-600 dark:text-accent-400" />
              </button>
            </div>
          )}
          <div className="flex justify-start w-full">
            <Button
              type="button"
              onClick={resetError}
              className="text-xs font-medium uppercase"
            >
              Go to Homepage
            </Button>
          </div>
        </div>
      </div>
    )
  }
  return ErrorFallback
}

const ClientErrorBoundary = (props: ClientErrorBoundaryProps) => (
  <ClientErrorBoundaryInner {...props} />
)

export default withRouter(withAuthInfo(ClientErrorBoundary))
